import { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ShieldCheck, ShieldX, FileImage, Clock } from 'lucide-react';

const DOC_LABEL = {
  carta_identita: "Carta d'identità",
  passaporto: 'Passaporto',
  patente: 'Patente di guida',
};

/**
 * Admin console — identity verification requests ("Proprietario Verificato").
 * Mostra lista richieste con filtro status (pending/verified/rejected/all) + preview
 * del documento + azioni approve/reject.
 */
export default function AdminVerifications() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [legacy, setLegacy] = useState([]); // vecchio flusso request_verification
  const [preview, setPreview] = useState(null); // { user_id, name, email, docUrl }
  const [docBlobUrl, setDocBlobUrl] = useState(null);
  const [docError, setDocError] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/admin/identity-requests', { params: { status: tab } });
      setItems(r.data || []);
    } catch {}
    if (tab === 'pending') {
      try {
        const r2 = await api.get('/admin/verifications');
        setLegacy(r2.data || []);
      } catch {}
    } else {
      setLegacy([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);

  // Load admin doc preview as a blob (sends auth header via api client)
  useEffect(() => {
    if (!preview?.user_id) return;
    let cancelled = false;
    setDocBlobUrl(null); setDocError(false);
    api.get(`/admin/users/${preview.user_id}/identity-doc`, { responseType: 'blob' })
      .then(r => { if (!cancelled) setDocBlobUrl(URL.createObjectURL(r.data)); })
      .catch(() => { if (!cancelled) setDocError(true); });
    return () => {
      cancelled = true;
      if (docBlobUrl) URL.revokeObjectURL(docBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.user_id]);

  const approve = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/identity-approve`, { reason: 'Documento conforme' });
      toast.success(`${u.name} ora è "Proprietario Verificato"`);
      setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openReject = (u) => { setRejectFor(u); setRejectReason(''); };

  const confirmReject = async () => {
    if (!rejectFor) return;
    try {
      await api.post(`/admin/users/${rejectFor.user_id}/identity-reject`,
        { reason: rejectReason || 'Documento non leggibile o non conforme' });
      toast('Richiesta rifiutata');
      setRejectFor(null); setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Legacy flow (old verification_status: 'pending' — pre-identity-doc)
  const legacyVerify = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/verify`, { reason: 'Verifica manuale approvata' }); toast.success('Utente verificato'); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const legacyReject = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/unverify`, { reason: 'Verifica rifiutata' }); toast('Richiesta chiusa'); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Verifiche identità</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Proprietario Verificato</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Approva o rifiuta i documenti d'identità caricati dai proprietari. Il badge
          "Proprietario Verificato" compare sugli annunci solo dopo approvazione manuale.
        </p>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { v: 'pending', l: 'In attesa', I: Clock },
          { v: 'verified', l: 'Approvati', I: ShieldCheck },
          { v: 'rejected', l: 'Rifiutati', I: ShieldX },
          { v: 'all', l: 'Tutti', I: null },
        ].map(t => (
          <button key={t.v} onClick={() => setTab(t.v)}
                  data-testid={`admin-ident-tab-${t.v}`}
                  className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border ${tab === t.v ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-secondary'}`}>
            {t.I && <t.I size={12}/>} {t.l}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card">
        {items.length === 0 && legacy.length === 0 ? (
          <div className="p-10 text-sm text-muted-foreground text-center">Nessuna richiesta in questa coda</div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map(u => (
              <li key={u.user_id} className="p-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {u.name}
                    <span className="text-xs text-muted-foreground">({u.email})</span>
                    {u.verification_priority && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-chartreuse/15 border border-chartreuse/40" data-testid={`verif-priority-${u.user_id}`}>Prioritaria · Ambassador</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {DOC_LABEL[u.identity_doc_type] || u.identity_doc_type || '—'}
                    {u.identity_full_name && <> · intestato a <b>{u.identity_full_name}</b></>}
                    {u.identity_submitted_at && <> · caricato il {new Date(u.identity_submitted_at).toLocaleDateString('it-IT')}</>}
                  </div>
                  {u.identity_status === 'rejected' && u.identity_rejection_reason && (
                    <div className="text-xs text-destructive mt-1">Motivo rifiuto: {u.identity_rejection_reason}</div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setPreview({
                    user_id: u.user_id, name: u.name, email: u.email, doc_type: u.identity_doc_type,
                    full_name: u.identity_full_name, status: u.identity_status,
                  })} data-testid={`admin-ident-view-${u.user_id}`}>
                    <FileImage size={12} className="mr-1"/> Vedi documento
                  </Button>
                  {u.identity_status === 'pending' && <>
                    <Button size="sm" onClick={() => approve(u)} data-testid={`admin-ident-approve-${u.user_id}`}>
                      <ShieldCheck size={12} className="mr-1"/> Approva
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openReject(u)} data-testid={`admin-ident-reject-${u.user_id}`}>
                      Rifiuta
                    </Button>
                  </>}
                </div>
              </li>
            ))}

            {/* Legacy requests (pre-identity-doc flow) — only shown in pending tab */}
            {legacy.map(u => (
              <li key={`legacy-${u.user_id}`} className="p-5 flex items-center justify-between gap-3 flex-wrap bg-secondary/20">
                <div>
                  <div className="font-medium">{u.name} <span className="text-xs text-muted-foreground">(legacy)</span></div>
                  <div className="text-xs text-muted-foreground">{u.email} · richiesta profilo (senza documento) il {new Date(u.created_at).toLocaleDateString('it-IT')}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => legacyVerify(u)}><ShieldCheck size={12} className="mr-1"/> Verifica profilo</Button>
                  <Button size="sm" variant="ghost" onClick={() => legacyReject(u)}>Rifiuta</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Doc preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Documento di {preview?.name}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                {DOC_LABEL[preview.doc_type] || preview.doc_type}
                {preview.full_name && <> · intestato a <b>{preview.full_name}</b></>}
                {' · '}{preview.email}
              </div>
              <div className="rounded-xl overflow-hidden border border-border bg-muted min-h-[300px] flex items-center justify-center">
                {docError ? (
                  <div className="p-10 text-sm text-muted-foreground">
                    Impossibile caricare il documento. Verifica di essere autenticato come admin.
                  </div>
                ) : docBlobUrl ? (
                  <img src={docBlobUrl} alt="documento" className="max-h-[70vh] w-auto object-contain"/>
                ) : (
                  <div className="p-10 text-sm text-muted-foreground">Caricamento documento…</div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Confronta nome del documento con "{preview.full_name || preview.name}" e la foto con il
                profilo pubblico. Rifiuta se illeggibile, scaduto, non di persona fisica, o diverso dall'intestatario.
              </p>
            </div>
          )}
          <DialogFooter>
            {preview?.status === 'pending' ? <>
              <Button variant="ghost" onClick={() => openReject({ user_id: preview.user_id, name: preview.name })}>Rifiuta</Button>
              <Button onClick={() => approve({ user_id: preview.user_id, name: preview.name })}>
                <ShieldCheck size={13} className="mr-2"/> Approva verifica
              </Button>
            </> : <Button variant="outline" onClick={() => setPreview(null)}>Chiudi</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rifiuta richiesta — {rejectFor?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Motivo del rifiuto (verrà mostrato all'utente nella sua dashboard):
          </p>
          <Textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="Es. Foto sfocata, documento scaduto, nome non corrispondente al profilo…"
                    data-testid="admin-ident-reject-reason"/>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>Annulla</Button>
            <Button onClick={confirmReject} data-testid="admin-ident-reject-confirm">Conferma rifiuto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
